import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcSweepsClearingV1SweepOrderQueryServiceSweepOrderGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweep-order-query-service-sweep-order-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcSweepsClearingV1SweepOrderState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { SweepDeferredEventsTable } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-deferred-events-table';
import { SweepOrderEventDrawer, SweepOrderEventSummary } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-event';
import {
  getSweepInitiatorDetailListItems,
  getSweepOrderStateListItems,
} from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-list-items';
import { getSweepOrderDetailPath } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-routing';
import { SweepOrderStatusPill } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-status-pill';
import { SweepOrderSummaryBar } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-summary-bar';
import { SweepOrderTypePill } from '../../../../../../../../../../features/sweeps/clearing/order/sweep-order-type-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/sweep/order/_order/$sweepOrderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-order-${params.sweepOrderId}`,
        label: 'Order',
        to: getSweepOrderDetailPath(params.sweepOrderId),
        suffix: (
          <CopyButton
            content={params.sweepOrderId}
            label="Copy Sweep Order ID"
            tooltip={{ copy: params.sweepOrderId, copied: 'Copied Sweep Order ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Order'),
  component: SweepOrderDetail,
});

function getListItems(data: IxcSweepsClearingV1SweepOrderState | undefined) {
  const listItems = getSweepOrderStateListItems(data);

  return {
    general: {
      left: [
        listItems.data.quantity,
        listItems.data.fundFamily,
        ...(listItems.data.reason ? [listItems.data.reason] : []),
        ...getSweepInitiatorDetailListItems(listItems.data.initiator),
      ] satisfies ReadonlyListItem[],
      right: [
        listItems.metadata.createdAt,
        listItems.data.settlementDate,
        listItems.sweepOrderId,
        listItems.data.adapterOrderId,
        listItems.data.accountId,
        listItems.data.sweepTargetId,
        listItems.data.assetId,
        listItems.data.completeTimestamp,
        ...(listItems.data.sweepsCutoffTimestamp ? [listItems.data.sweepsCutoffTimestamp] : []),
      ] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(sweepOrderId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: SweepOrderEventDrawer,
        props: { request: { sweepOrderId } },
      },
    },
    actions: [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep order', 'view this sweep order');

function SweepOrderDetail() {
  const { sweepOrderId } = Route.useParams();
  const request = { sweepOrderId } as const;
  const { data, error, isPending } = useIxcSweepsClearingV1SweepOrderQueryServiceSweepOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepOrder), [data?.sweepOrder]);
  const menuItems = useMemo(() => getMenuItems(sweepOrderId), [sweepOrderId]);
  const initiator = data?.sweepOrder?.data?.initiator;
  const cashBalanceCatchUp = initiator && 'cashBalanceCatchUp' in initiator ? initiator.cashBalanceCatchUp : undefined;

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepOrderStatusPill isPending={showPending} size="large" status={data?.sweepOrder?.status} />
              <SweepOrderTypePill isPending={showPending} orderType={data?.sweepOrder?.data?.orderType} size="large" />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <SweepOrderSummaryBar sweepOrderId={sweepOrderId} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {cashBalanceCatchUp ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Deferred Events</Typography.Level3>
            <SweepDeferredEventsTable
              accountId={data?.sweepOrder?.data?.accountId}
              deferredCount={cashBalanceCatchUp.deferredCount}
              deferredEvents={cashBalanceCatchUp.deferredEvents ?? STABLE_EMPTY_ARRAY_REF}
            />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
